import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useQuery<T>(
  table: string,
  filters: Record<string, any> = {},
  options: { single?: boolean; withRelations?: string } = {}
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let query = supabase
          .from(table)
          .select(options.withRelations || '*');

        // Apply filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'orderBy') {
              query = query.order(value);
            } else {
              query = query.eq(key, value);
            }
          }
        });

        let result;
        let queryError;

        if (options.single) {
          try {
            const { data: singleResult, error: singleError } = await query.single();
            result = singleResult;
            queryError = singleError;
          } catch (err) {
            // Si l'erreur est PGRST116 (multiple rows returned), on récupère la première ligne
            if (err && typeof err === 'object' && 'code' in err && err.code === 'PGRST116') {
              console.warn('Multiple rows returned for single() query, using first row');
              const { data: multipleResult, error: multipleError } = await query.limit(1);
              result = multipleResult?.[0] || null;
              queryError = multipleError;
            } else {
              throw err;
            }
          }
        } else {
          const { data: multipleResult, error: multipleError } = await query;
          result = multipleResult;
          queryError = multipleError;
        }

        if (queryError) {
          throw queryError;
        }

        setData(result as T);
        setError(null);
      } catch (err) {
        console.error('Query error:', err);
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [table, JSON.stringify(filters), options.single, options.withRelations]);

  return { data, loading, error };
}